import { SyncOperationText } from "@/components/sync/SyncOperationText";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { AppStore } from "@/store/AppStore";
import { NoteObservable } from "@/store/note";
import { NoteModelData, NoteUpsertedSyncUpdateValue } from "@/store/note/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { AbstractSyncErrorModalFieldsGenerator } from "@/store/sync/operations/errors/SyncErrorModalFields";
import { IRestoreNoteOperation } from "@/store/sync/operations/types";
import { SyncCustomErrorData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";
import { getLoggableOperation } from "@/store/sync/utils";

export class RestoreNoteOperation extends BaseSyncOperation<IRestoreNoteOperation> {
  private existingModel?: NoteObservable;
  get operationKind(): SyncOperationKind {
    return "RESTORE_NOTE";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<NoteModelData>[]> {
    const existingNote = this.existingModel;
    if (!existingNote) return [];

    const value: NoteUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "NOTE",
      model_version: existingNote.modelVersion,
      model_data: {
        ...existingNote.modelData,
        trashed_at: null,
      },
      model_scopes: existingNote.modelScopes,
    };
    const syncUpdate: OptimisticSyncUpdate<NoteModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };
    return [syncUpdate];
  }

  async execute() {
    this.existingModel = await this.store.notes.getAsync(this.payload.id);
    if (!this.existingModel) {
      logger.error({
        message: "can't find existing note",
        info: {
          operation: getLoggableOperation(this),
        },
      });
    }

    await super.execute();
  }

  async triggerRecompute(): Promise<void> {
    await this.store.notes.recompute(this.payload.id);
  }

  get successToastMessage() {
    const note = this.store.notes.get(this.payload.id);
    if (!note?.isOwnedByMe || !this.triggerSuccessToast) return null;
    return (
      <>
        <SyncOperationText>{note.title || UNTITLED_NOTE_TITLE}</SyncOperationText> restored from{" "}
        <a onClick={() => this.store.navigation.goToTrash()}>Trash</a>
      </>
    );
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.triggerModal(this.getSyncErrorModalFieldsGenerator("INVALID"));
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerModal(this.getSyncErrorModalFieldsGenerator("PERMISSION_DENIED"));
  }

  get syncErrorModalFieldsGenerator() {
    return this.getSyncErrorModalFieldsGenerator("UNKNOWN");
  }

  private getSyncErrorModalFieldsGenerator(kind: SyncCustomErrorData["kind"]) {
    return (store: AppStore) => new RestoreNoteSyncErrorModalFieldsGenerator(store, this.id, kind);
  }
}

class RestoreNoteSyncErrorModalFieldsGenerator extends AbstractSyncErrorModalFieldsGenerator {
  protected id: string;
  protected kind: SyncCustomErrorData["kind"];

  constructor(store: AppStore, id: string, kind: SyncCustomErrorData["kind"]) {
    super(store);

    this.id = id;
    this.kind = kind;
  }

  get includeContactSupportButton() {
    return this.kind === "UNKNOWN";
  }

  get title() {
    const note = this.store.notes.get(this.id);
    switch (this.kind) {
      case "INVALID":
      case "UNKNOWN":
        return `“${note?.title || UNTITLED_NOTE_TITLE}” could not be restored`;
      case "PERMISSION_DENIED":
        return `You do not have permissions to restore “${note?.title || UNTITLED_NOTE_TITLE}”`;
    }
  }

  get message() {
    switch (this.kind) {
      case "INVALID":
        return `The note was permanently deleted`;
      case "PERMISSION_DENIED":
        return `Please contact the note owner`;
      case "UNKNOWN":
        return `If this error continues, please contact support`;
    }
  }

  modalActionHandler = async () => {
    this.store.sync.actionQueue.skipAndRevertOperationById(this.id);
  };
}
